import type { LineItemVariant, PurchaseOrderUnsavedLineItemPreview } from "../../api_utils/types";
import EditorContent from "../common/EditorContent";
import { useLineItemRequests } from "../../hooks/useLineItemRequests";
import type { PurchaseOrderResponse } from "../../api_utils/types";
import { appendVariantLineItems, sortedVariantLineItems, removedVariantIds, variantLineItemIdentity, variantLineItemsMap } from "../../helper_functions/variantLineItems";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { TitleBar } from "@shopify/app-bridge-react";
import {
  Card,
  Page,
  Badge,
  TextField,
  IndexTable,
  EmptyState,
  Layout,
  PageActions,
  Button,
  Text,
  Banner,
  BlockStack,
  InlineGrid,
  Box,
  InlineStack,
  Tooltip,
} from "@shopify/polaris";
import type { IndexFiltersProps } from "@shopify/polaris";
import primaryFillImage from "images/primary-fill.svg";
import EmailPdfDialog from "./support/EmailPdfDialog";
import AddVariantDialog from "../common/AddVariantDialog";
import CostAdjustments from "./support/CostAdjustments";
import { useEditableCostAdjustments } from "./support/useEditableCostAdjustments";
import { useReceivedVariantRemoval } from "./support/useReceivedVariantRemoval";
import {
  deletePurchaseOrder,
  getExchangeRate,
  getPurchaseOrderHorseVariants,
  previewUnsavedPurchaseOrderLineItems,
  updatePurchaseOrder,
  getRecommendations,
  importPurchaseOrderLineItems,
  emailPdf,
  downloadFile,
  useShopifyShop,
  useUser,
} from "../../api_utils/requests";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import ForecastingForm from "./support/ForecastingForm";
import {
  formatMoney,
  roundToTwoDecimals,
  commaDelimiter,
  assertString,
  assertNumber,
  softAssertNumber,
  extractMessageFromError,
  omit,
  cloneNestedObjects,
} from "../../helper_functions/utils";
import { ImportFile } from "../common/ImportFile";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import { getTotal, KEYS } from "./support/utils";
import type { EditablePurchaseOrderLineItem, IPurchaseOrderLineItem, PurchaseOrderLineItems, PurchaseOrderUpdatePayload } from "../../api_utils/types";
import {
  PurchaseOrderState,
  type IPurchaseOrder,
  type PurchaseOrderLineItemsQueryParams,
  type PurchaseOrderLineItemUpdatePayload,
  type RecommendationQueryParams,
} from "../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { PURCHASE_ORDER_HELP_PAGE_URL } from "../../constants";
import PurchaseOrderDraftRow from "./support/PurchaseOrderDraftRow";
import PurchaseOrderEditCard from "./PurchaseOrderEditCard";
import { useQuery } from "@tanstack/react-query";
import SaveBar from "../common/SaveBar";
import Comments from "./Comments";
import { useNavigate } from "react-router-dom";
import CopyRecommendationsButton from "./support/CopyRecommendationsButton";

import TableControls from "../common/TableControls";
import { useVisibleColumns } from "../../hooks/useVisibleColumns";
import { draftColumns } from "./support/optionalColumns";

const getPurchaseOrderLineItemsPayload = (
  purchaseOrderLineItems: PurchaseOrderLineItems,
): PurchaseOrderLineItemUpdatePayload[] =>
  sortedVariantLineItems(purchaseOrderLineItems).map(
    (purchaseOrderLineItem: EditablePurchaseOrderLineItem): PurchaseOrderLineItemUpdatePayload => {
      return {
        ...variantLineItemIdentity(purchaseOrderLineItem),
        quantity: purchaseOrderLineItem.quantity,
        cost: softAssertNumber(purchaseOrderLineItem.cost),
      } satisfies PurchaseOrderLineItemUpdatePayload;
    },
  );

const assertDate = (date: Date | string, fallback: Date): Date => (date ? new Date(date) : fallback);

const notValidatePurchaseOrderValue = (quantities: PurchaseOrderLineItems): boolean =>
  Object.keys(quantities).some((key) => !quantities[key].quantity || quantities[key].quantity.toString() === "0");

const sortOptions = [
  { label: "Created", value: "created_at asc", directionLabel: "Oldest to newest" },
  { label: "Created", value: "created_at desc", directionLabel: "Newest to oldest" },
  { label: "Title", value: "product_title asc", directionLabel: "A–Z" },
  { label: "Title", value: "product_title desc", directionLabel: "Z–A" },
  { label: "SKU", value: "sku asc", directionLabel: "A–Z" },
  { label: "SKU", value: "sku desc", directionLabel: "Z–A" },
  { label: "Available", value: "currently_available asc", directionLabel: "Low to high" },
  { label: "Available", value: "currently_available desc", directionLabel: "High to low" },
  { label: "Ordered", value: "ordered_quantity asc", directionLabel: "Low to high" },
  { label: "Ordered", value: "ordered_quantity desc", directionLabel: "High to low" },
  { label: "Sales rate", value: "sales_rate asc", directionLabel: "Low to high" },
  { label: "Sales rate", value: "sales_rate desc", directionLabel: "High to low" },
  { label: "Recommend", value: "needed asc", directionLabel: "Low to high" },
  { label: "Recommend", value: "needed desc", directionLabel: "High to low" },
  { label: "Stockout date", value: "oos_date asc", directionLabel: "Earliest to latest" },
  { label: "Stockout date", value: "oos_date desc", directionLabel: "Latest to earliest" },
  { label: "Cost", value: "cost asc", directionLabel: "Low to high" },
  { label: "Cost", value: "cost desc", directionLabel: "High to low" },
  { label: "Quantity", value: "quantity asc", directionLabel: "Low to high" },
  { label: "Quantity", value: "quantity desc", directionLabel: "High to low" },
  { label: "Line total", value: "line_total asc", directionLabel: "Low to high" },
  { label: "Line total", value: "line_total desc", directionLabel: "High to low" },
] satisfies IndexFiltersProps["sortOptions"];

const calculateRecommendedQuantity = (poli: IPurchaseOrderLineItem | EditablePurchaseOrderLineItem): number => {
  let {relevant_purchase_order_quantity} = poli;
  if (relevant_purchase_order_quantity === undefined || relevant_purchase_order_quantity === null) {
    relevant_purchase_order_quantity = poli.ordered_quantity || 0;
  }
  let recommendedQuantity = poli.needed || 0;
  if (poli.min && poli.min > recommendedQuantity && (!poli.minimum_order_quantity || poli.min > poli.minimum_order_quantity)) {
    const minRecommendedQuantity = poli.min - (poli.currently_available || 0) - (relevant_purchase_order_quantity || 0);
    recommendedQuantity = Math.max(recommendedQuantity, minRecommendedQuantity);
  } else if (poli.minimum_order_quantity && poli.minimum_order_quantity > recommendedQuantity) {
    recommendedQuantity = poli.minimum_order_quantity;
  }
  return recommendedQuantity;
};

const formatLineItems = (lineItems: IPurchaseOrderLineItem[]): PurchaseOrderLineItems =>
  variantLineItemsMap(lineItems.map((lineItem) => ({
    ...lineItem,
    cost: lineItem.cost === null ? "" : new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2, maximumFractionDigits: 4,
    }).format(assertNumber(lineItem.cost)),
    quantity: String(lineItem.quantity ?? 0),
    recommended_quantity: calculateRecommendedQuantity(lineItem),
  })));

export default function Index({
  response: initialResponse,
  onSave,
  currencies,
}: {
  readonly response: PurchaseOrderResponse;
  readonly onSave: (response: PurchaseOrderResponse) => void;
  readonly currencies: string[];
}): React.ReactElement {
  const initialPurchaseOrder = initialResponse.purchaseOrder;
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns("purchase-orders-draft", draftColumns);
  const visibleTrailingColumns = visibleColumns.filter((column) => column.id !== "ordered_quantity" && column.id !== "oos_date");
  const showOrderedQuantity = visibleColumns.some((column) => column.id === "ordered_quantity");
  const showStockoutDate = visibleColumns.some((column) => column.id === "oos_date");
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    shopify.loading(isLoading);
  }, [isLoading]);

  // State
  const [isDirty, setIsDirty] = useState(false);
  const hasPendingChanges = useRef(isDirty);
  hasPendingChanges.current = isDirty;
  const { data: shopifyShop } = useShopifyShop();
  const { readyForRecommendations } = shopifyShop;
  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);
  const [sortedValue, setSortedValue] = useState("created_at-asc");
  const handleSortChange = useCallback(([sort]: string[]): void => {
    if (isLoading) return;
    setSortedValue(sort.replace(" ", "-"));
  }, [isLoading]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);
  const [addVariantModalActive, setAddVariantModalActive] = useState(false);
  const [emailPdfDialogActive, setEmailPdfDialogActive] = useState(false);
  const [editVendorOrSupplier, setEditVendorOrSupplier] = useState<boolean>(false);
  const [editDestination, setEditDestination] = useState<boolean>(false);

  const handleEmailPdfModalClose = useCallback(() => {
    setEmailPdfDialogActive(false);
  }, []);
  const openImportCSVModal = useCallback(() => {
    setImportCSVModalIsOpen(true);
  }, []);
  const openDeletionModal = useCallback(() => {
    setDeletionDialogueActive(true);
  }, []);
  const openEmailPdfModal = useCallback(() => {
    setEmailPdfDialogActive(true);
  }, []);
  const openAddVariantModal = useCallback(() => {
    setAddVariantModalActive(true);
  }, []);

  // Purchase Order
  const defaultArrivalDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date;
  }, []);
  const estimatedArrivalDate = assertDate(initialPurchaseOrder?.arrival_date, defaultArrivalDate);
  const initialPurchaseOrderWithArrivalDate = {
    ...initialPurchaseOrder,
    arrival_date: estimatedArrivalDate,
  };
  const resetPurchaseOrder = useRef<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const salesChannelIds = purchaseOrder.sale_shopify_channel_ids;
  const includeAllOutOfStockVariants = purchaseOrder.include_all_out_of_stock_variants;
  const purchaseOrderCurrency = purchaseOrder.currency || userCurrency;
  const currenciesMatch = purchaseOrderCurrency === userCurrency;
  const { data: exchangeRate } = useQuery({
    enabled: !currenciesMatch,
    queryFn: () => getExchangeRate(purchaseOrderCurrency, userCurrency),
    queryKey: ["exchangeRate", purchaseOrderCurrency, userCurrency],
  });
  const costExchangeRate = currenciesMatch ? 1 : exchangeRate?.rate;

  const [selectedForecastingMethod, setSelectedForecastingMethod] = useState<"sales_rate" | "seasonal">(
    initialPurchaseOrder.forecasting_method || "sales_rate",
  );

  const [selectedSamplingPeriod, setSelectedSamplingPeriod] = useState<number>(
    initialPurchaseOrder.sampling_period || 90,
  );

  // PurchaseOrderLineItems
  const [purchaseOrderLineItems, setPurchaseOrderLineItems] = useState<PurchaseOrderLineItems>(
    () => formatLineItems(initialResponse.line_items),
  );
  const resetPolis = useRef(purchaseOrderLineItems);

  const [errorMessage, setErrorMessage] = useState("");
  const { request, cancel } = useLineItemRequests(setErrorMessage);
  const [autoAddMessage, setAutoAddMessage] = useState<string>("");
  const clearAutoAddMessage = useCallback(() => {
    setAutoAddMessage("");
  }, []);

  const arrivalDate = purchaseOrder.arrival_date.toISOString();
  const query = useMemo<PurchaseOrderLineItemsQueryParams>(
    () => ({
      sampling_period: selectedSamplingPeriod,
      arrival_date: new Date(arrivalDate),
      horse_location_id: purchaseOrder.horse_location_id,
      supplier_id: purchaseOrder.supplier_id,
      vendor: purchaseOrder.vendor,
      currency: purchaseOrderCurrency,
      days_of_inventory: purchaseOrder.days_of_inventory,
      forecasting_method: selectedForecastingMethod,
      by_sort: sortedValue,
      sale_shopify_channel_ids: salesChannelIds,
    }),
    [
      selectedSamplingPeriod,
      arrivalDate,
      purchaseOrder.horse_location_id,
      purchaseOrder.supplier_id,
      purchaseOrder.vendor,
      purchaseOrderCurrency,
      purchaseOrder.days_of_inventory,
      selectedForecastingMethod,
      sortedValue,
      salesChannelIds,
    ],
  );

  // Only run this update on update
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!isInitialMount.current && !isLoading) {
      // manually send the resize event every time the table is updated to fix the table scrollbars
      const event = new Event("resize");
      window.dispatchEvent(event);
    }
    isInitialMount.current = false;
  }, [isLoading]);

  const acceptLineItems = useCallback((response: IPurchaseOrderLineItem[]): void => {
    const savedLineItems = formatLineItems(response);
    resetPolis.current = savedLineItems;
    setPurchaseOrderLineItems(savedLineItems);
  }, []);

  const applyUnsavedLineItemsPreview = useCallback((previews: Record<number, PurchaseOrderUnsavedLineItemPreview>): void => {
    setPurchaseOrderLineItems((lineItems) => Object.fromEntries(Object.entries(lineItems).map(([id, lineItem]) => {
      const preview = previews[id];
      if (!preview) return [id, lineItem];
      const updatedLineItem = {
        ...lineItem, ...preview,
        cost: lineItem.cost === "" && preview.cost !== null ? String(preview.cost) : lineItem.cost,
      };
      return [id, { ...updatedLineItem, recommended_quantity: calculateRecommendedQuantity(updatedLineItem) }];
    })));
  }, []);

  const previewUnsavedLineItems = useCallback((horseVariantIds: number[]): void => {
    if (horseVariantIds.length === 0) return;
    void request((signal) => previewUnsavedPurchaseOrderLineItems(purchaseOrder.id, horseVariantIds, query, signal), applyUnsavedLineItemsPreview);
  }, [request, purchaseOrder.id, query, applyUnsavedLineItemsPreview]);

  const addVariants = useCallback((horseVariants: LineItemVariant[]): void => {
    const addedLines = variantLineItemsMap(horseVariants.map((horseVariant): EditablePurchaseOrderLineItem =>
      resetPolis.current[horseVariant.id] || {
        ...horseVariant, horse_variant_id: horseVariant.id, id: null,
        quantity: "0", cost: "", received: 0, rejected: 0,
        currently_available: null, ordered_quantity: null, needed: null, oos_date: null,
        sales_rate: null, sales_quantity: null, available_days: null, relevant_purchase_order_quantity: null,
        recommended_quantity: null, line_total: 0, created_at: null, shopify_image_id: null,
        description_of_goods: null, entry_summary_line_number: null, classification_number: null, duty_rate: null,
        sortIndex: 0,
      },
    ));
    setPurchaseOrderLineItems((lineItems) => appendVariantLineItems(lineItems, addedLines));
    setIsDirty(true);
    previewUnsavedLineItems(horseVariants.filter(({ id }) => !resetPolis.current[id]).map(({ id }) => id));
  }, [previewUnsavedLineItems]);

  const currentLineItems = useRef(purchaseOrderLineItems);
  currentLineItems.current = purchaseOrderLineItems;
  const previousQuery = useRef(JSON.stringify(query));
  useEffect(() => {
    if (previousQuery.current === JSON.stringify(query)) return;
    previousQuery.current = JSON.stringify(query);
    cancel();
    setLoadingTable(true);
    void request((signal) => getPurchaseOrderHorseVariants(purchaseOrder.id, query, signal), (response) => {
      const forecasts = variantLineItemsMap(response);
      setPurchaseOrderLineItems((lineItems) => Object.fromEntries(Object.entries(lineItems).map(([id, lineItem]) => {
        const forecast = forecasts[id];
        if (lineItem.id === null || !forecast) return [id, lineItem];
        const updatedLineItem = {
          ...lineItem,
          currently_available: forecast.currently_available, oos_date: forecast.oos_date,
          sales_rate: forecast.sales_rate, sales_quantity: forecast.sales_quantity, available_days: forecast.available_days,
          needed: forecast.needed, ordered_quantity: forecast.ordered_quantity,
          relevant_purchase_order_quantity: forecast.relevant_purchase_order_quantity,
          justification: forecast.justification,
          supplier_cost: forecast.supplier_cost, supplier_sku: forecast.supplier_sku,
          minimum_order_quantity: forecast.minimum_order_quantity, pack_size: forecast.pack_size,
          sortIndex: hasPendingChanges.current ? lineItem.sortIndex : forecast.sortIndex,
        };
        return [id, { ...updatedLineItem, recommended_quantity: calculateRecommendedQuantity(updatedLineItem) }];
      })));
    }).finally(() => { setLoadingTable(false); });
    previewUnsavedLineItems(Object.values(currentLineItems.current).filter(({ id }) => id === null).map(({ horse_variant_id }) => horse_variant_id));
  }, [query, purchaseOrder.id, request, cancel, previewUnsavedLineItems]);

  const handleNoteChange = useCallback(
    (note: string) => {
      setPurchaseOrder({ ...purchaseOrder, note });
      setIsDirty(true);
    },
    [purchaseOrder],
  );

  const removeVariants = (horseVariantIds: number[]): void => {
    const removedIds = new Set(horseVariantIds);
    setPurchaseOrderLineItems((lineItems) => Object.fromEntries(
      Object.entries(lineItems).filter(([id]) => !removedIds.has(Number(id))),
    ));
    setIsDirty(true);
  };

  const { removeVariant, removalWarning, removalConfirmation } =
    useReceivedVariantRemoval(purchaseOrderLineItems, removeVariants, isLoading);

  const total = useMemo(() => getTotal(purchaseOrderLineItems), [purchaseOrderLineItems]);
  const {
    costAdjustments,
    setCostAdjustments,
    total: costAdjustmentsTotal,
    attributes: costAdjustmentsAttributes,
    accept: acceptCostAdjustments,
    discard: discardCostAdjustments,
  } = useEditableCostAdjustments(initialResponse.cost_adjustments, total.totalCost);
  const setSummaryIsDirty = useCallback((summaryIsDirty: boolean): void => {
    setIsDirty(isDirty || summaryIsDirty);
  }, [isDirty]);

  const handleDiscardAction = (): void => {
    cancel();
    setPurchaseOrder(cloneNestedObjects(resetPurchaseOrder.current));
    setPurchaseOrderLineItems(cloneNestedObjects(resetPolis.current));
    setSelectedForecastingMethod(resetPurchaseOrder.current.forecasting_method || "sales_rate");
    setSelectedSamplingPeriod(resetPurchaseOrder.current.sampling_period || 90);
    setEditVendorOrSupplier(false);
    setEditDestination(false);
    setIsDirty(false);
    discardCostAdjustments();
  };

  const acceptResponse = useCallback((response: PurchaseOrderResponse): void => {
    const savedPurchaseOrder = { ...response.purchaseOrder, arrival_date: new Date(response.purchaseOrder.arrival_date) };
    previousQuery.current = JSON.stringify({
      ...query,
      sampling_period: savedPurchaseOrder.sampling_period,
      arrival_date: savedPurchaseOrder.arrival_date,
      horse_location_id: savedPurchaseOrder.horse_location_id,
      supplier_id: savedPurchaseOrder.supplier_id,
      vendor: savedPurchaseOrder.vendor,
      currency: savedPurchaseOrder.currency,
      days_of_inventory: savedPurchaseOrder.days_of_inventory,
      forecasting_method: savedPurchaseOrder.forecasting_method,
    });
    resetPurchaseOrder.current = savedPurchaseOrder;
    setPurchaseOrder(savedPurchaseOrder);
    setSelectedForecastingMethod(savedPurchaseOrder.forecasting_method);
    setSelectedSamplingPeriod(savedPurchaseOrder.sampling_period);
    acceptLineItems(response.line_items);
    acceptCostAdjustments(response.cost_adjustments);
    setIsDirty(false);
    setEditVendorOrSupplier(false);
    setEditDestination(false);
  }, [acceptLineItems, acceptCostAdjustments, query]);

  const getPayload = useCallback(
    (): PurchaseOrderUpdatePayload => ({
      ...omit(purchaseOrder, ["id", "destination", "created_at", "updated_at", "supplier", "vendorSettings"]),
      purchase_order_line_items_attributes: getPurchaseOrderLineItemsPayload(purchaseOrderLineItems),
      cost_adjustments_attributes: costAdjustmentsAttributes,
      removed_horse_variant_ids: removedVariantIds(resetPolis.current, purchaseOrderLineItems),
      forecasting_method: selectedForecastingMethod,
      sampling_period: selectedSamplingPeriod,
      arrival_date: purchaseOrder.arrival_date,
      days_of_inventory: purchaseOrder.days_of_inventory || 90,
    }),
    [purchaseOrder, purchaseOrderLineItems, selectedForecastingMethod, selectedSamplingPeriod, costAdjustmentsAttributes],
  );

  const handleMarkAsOrdered = useCallback((): void => {
    if (notValidatePurchaseOrderValue(purchaseOrderLineItems)) {
      setErrorMessage("Quantity must be greater or equal to 1");
      return;
    }
    cancel();
    setIsLoading(true);
    const mPayload = {
      purchase_order: {
        ...getPayload(),
        state: PurchaseOrderState.Ordered,
      },
    };
    updatePurchaseOrder(purchaseOrder.id, mPayload)
      .then((response) => {
        acceptResponse(response);
        onSave(response);
        shopify.toast.show("Save successful");
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, mPayload);
        console.error(err);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getPayload, purchaseOrder.id, onSave, purchaseOrderLineItems, acceptResponse, cancel]);

  const handleSaveDraft = useCallback(async (): Promise<void> => {
    cancel();
    setIsLoading(true);

    const payload = getPayload();

    try {
      const response = await updatePurchaseOrder(purchaseOrder.id, { purchase_order: payload }, query);
      acceptResponse(response);
      onSave(response);
      shopify.toast.show("Save successful");
    } catch (err: unknown) {
        Rollbar.error(err, { payload });
        console.error(err);

        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [getPayload, purchaseOrder.id, onSave, acceptResponse, query, cancel]);

  const handleAutofill = useCallback(
    ({
      percentIncrease,
      quantityIncrease,
    }: {
      percentIncrease?: number;
      quantityIncrease?: number;
    } = {}): void => {
      setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
        return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
          acc[lineItem.horse_variant_id] = lineItem;
          if (lineItem.recommended_quantity === null) return acc;
          let newQuantity = Number(lineItem.needed || "0");
          newQuantity = Math.max(newQuantity, 0);
          if (lineItem.min) {
            const minNeeded = lineItem.min - (lineItem.currently_available || 0) - (lineItem.relevant_purchase_order_quantity || 0);
            newQuantity = Math.max(newQuantity, minNeeded);
          }
          if (percentIncrease) {
            newQuantity = Math.ceil(newQuantity * (1 + percentIncrease / 100));
          }
          if (quantityIncrease) {
            newQuantity = Math.ceil(newQuantity + quantityIncrease);
          }
          if (lineItem.minimum_order_quantity && newQuantity < lineItem.minimum_order_quantity) {
            newQuantity = lineItem.minimum_order_quantity;
          }
          if (lineItem.pack_size) {
            newQuantity = Math.ceil(newQuantity / lineItem.pack_size) * lineItem.pack_size;
          }
          acc[lineItem.horse_variant_id] = { ...lineItem, quantity: newQuantity.toString() };
          return acc;
        }, {});
      });
      setIsDirty(true);
    },
    [setIsDirty, setPurchaseOrderLineItems],
  );

  const copyBackorders = useCallback((): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        acc[lineItem.horse_variant_id] = lineItem;
        const itemQuantity = Number(lineItem.currently_available || "0");
        if (itemQuantity < 0) {
          acc[lineItem.horse_variant_id] = { ...lineItem, quantity: (-itemQuantity).toString() };
        }
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const setAllQuantities = useCallback((quantity: number): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        acc[lineItem.horse_variant_id] = { ...lineItem, quantity: quantity.toString() };
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const setFillQuantities = useCallback((fillToQuantity: number): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        acc[lineItem.horse_variant_id] = lineItem;
        if (lineItem.recommended_quantity === null) return acc;
        const deficit = fillToQuantity - (lineItem.currently_available || 0);
        const quantity = Math.max(0, deficit);
        acc[lineItem.horse_variant_id] = { ...lineItem, quantity: quantity.toString() };
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const handleFormFieldChange = useCallback(
    (
      key: (typeof KEYS)[keyof typeof KEYS],
      value: "sales_rate" | "seasonal" | number | "Any" | number[] | boolean,
    ): void => {
    if (isLoading) return;
    switch (key) {
      case KEYS.FORECASTING_METHOD: {
        setSelectedForecastingMethod(value as "sales_rate" | "seasonal");
        setIsDirty(true);
        break;
      }
      case KEYS.SAMPLING_PERIOD: {
        setSelectedSamplingPeriod(value as number);
        setIsDirty(true);
        break;
      }
      case KEYS.DAYS_OF_INVENTORY: {
        setPurchaseOrder({ ...purchaseOrder, days_of_inventory: value as number });
        setIsDirty(true);
        break;
      }
      case KEYS.SALES_CHANNEL_IDS: {
        setPurchaseOrder({ ...purchaseOrder, sale_shopify_channel_ids: value === "Any" ? [] : (value as number[]) });
        setIsDirty(true);
        break;
      }
      case KEYS.INCLUDE_ALL_OUT_OF_STOCK_VARIANTS: {
        setPurchaseOrder({ ...purchaseOrder, include_all_out_of_stock_variants: value as boolean });
        setIsDirty(true);
        break;
      }
      default:
        throw new Error(`Unknown key: ${key}, value: ${value.toString()}`);
    }
  }, [isLoading, purchaseOrder, setPurchaseOrder, setSelectedForecastingMethod, setSelectedSamplingPeriod]);

  const [autoAddLoading, setAutoAddLoading] = useState(false);
  const autoAdd = useCallback((queryParams: RecommendationQueryParams): void => {
    setAutoAddLoading(true);
    void request((signal) => getRecommendations(purchaseOrder.id, queryParams, signal), (horseVariants) => {
      const additions = horseVariants.filter(({ id }) => !currentLineItems.current[id]);
      if (additions.length === 0) {
        setAutoAddMessage("Horse doesn't have any more variants to recommend. You could try adjusting the recommendation settings.");
        return;
      }
      addVariants(additions);
    }).finally(() => { setAutoAddLoading(false); });
  }, [request, purchaseOrder.id, addVariants]);

  const emptyState = useMemo(() => (
    <EmptyState
      action={{
        content: "Add variants",
        onAction: openAddVariantModal,
      }}
      heading="No variants added"
      image=""
    >
      <p>You have not added any variants to your purchase order yet.</p>
    </EmptyState>
  ), [openAddVariantModal]);

  const purchaseOrderTotal = roundToTwoDecimals(total.totalCost + costAdjustmentsTotal);

  const sortedLineItems = sortedVariantLineItems(purchaseOrderLineItems);

  const headings = useMemo(() => [
    { title: "Variant" },
    { title: "Available", tooltipContent: "Currently available at the destination" },
    ...(showOrderedQuantity ? [{ title: "Ordered", tooltipContent: "Currently ordered quantity to this destination" }] : []),
    { title: selectedForecastingMethod === "seasonal" ? "seasonal" : "Sales rate", tooltipContent: "Based on the sampling period." },
    { title: "Recommendation", tooltipContent: "Based on the sampling period and days of inventory." },
    ...(showStockoutDate ? [{ title: "Stockout date", tooltipContent: "Date when the variant went out of stock" }] : []),
    { title: "Cost" },
    ...visibleTrailingColumns,
    { title: "Quantity" },
    { title: "Line total" },
    { title: "" },
  ].filter((item): boolean => item.title !== "seasonal") as NonEmptyArray<IndexTableHeading>, [
    selectedForecastingMethod,
    visibleTrailingColumns,
    showOrderedQuantity,
    showStockoutDate,
  ]);

  const setPoli = useCallback(
    (newPoli: EditablePurchaseOrderLineItem): void => {
      setPurchaseOrderLineItems((prev) => ({ ...prev, [newPoli.horse_variant_id]: newPoli }));
      setIsDirty(true);
    },
    [],
  );

  const handleEmailPdf = useCallback(
    async (id, emailPayload): Promise<void> => {
      await emailPdf(id, emailPayload, query);
    },
    [query],
  );

  const selectHorseVariantIds = useMemo(() => {
    return Object.values(purchaseOrderLineItems).map(({ horse_variant_id }) => horse_variant_id);
  }, [purchaseOrderLineItems]);

  const getQuery = useCallback((): RecommendationQueryParams => ({
    ...query,
    include_all_out_of_stock_variants: includeAllOutOfStockVariants,
  }), [query, includeAllOutOfStockVariants]);

  const handleAutoAdd = useCallback(() => {
    const recommendationQuery = getQuery();
    autoAdd(recommendationQuery);
  }, [autoAdd, getQuery]);

  const navigate = useNavigate();

  const handleExportPdf = () => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.pdf`;
    setIsLoading(true);
    downloadFile(`/purchase_orders/${purchaseOrder.id}.pdf`, filename, query)
      .then(() => {
        setIsLoading(false);
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, query);
        console.error(err);
        setIsLoading(false);
        setErrorMessage("Failed to generate PDF");
      });
  };

  const handleExportCsv = async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}.csv${window.location.search}`, filename);
  };

  const pageMarkup = (
    <Page fullWidth titleMetadata={<Badge tone="info">Draft</Badge>}>
      <TitleBar title="Draft purchase order">
        <button
          disabled={isLoading}
          onClick={async () => {
            await shopify.saveBar.leaveConfirmation();

            navigate("/purchase_orders");
          }}
          type="button"
          // eslint-disable-next-line react/no-unknown-property -- App Bridge TitleBar buttons take a variant attribute
          variant="breadcrumb"
        >
          Purchase orders
        </button>
        {/* eslint-disable-next-line react/no-unknown-property -- App Bridge TitleBar buttons take a variant attribute */}
        <button disabled={isLoading} onClick={handleMarkAsOrdered} type="button" variant="primary">
          Mark as ordered
        </button>
        <section label="More actions">
          <button disabled={isLoading} onClick={handleExportPdf} type="button">
            Export PDF
          </button>
          <button disabled={isLoading} onClick={openEmailPdfModal} type="button">
            Email PDF
          </button>
          <button disabled={isLoading} onClick={handleExportCsv} type="button">
            Export CSV
          </button>
          <button disabled={isDirty || isLoading} onClick={openImportCSVModal} type="button">
            Import CSV
          </button>
        </section>
        <button disabled={isLoading} onClick={openAddVariantModal} type="button">
          Add variants
        </button>
      </TitleBar>
      <SaveBar disabled={isLoading} handleDiscard={handleDiscardAction} handleSave={handleSaveDraft} isDirty={isDirty} />
      <EmailPdfDialog
        active={emailPdfDialogActive}
        onClose={handleEmailPdfModalClose}
        onSend={handleEmailPdf}
        purchaseOrder={purchaseOrder}
        setErrorMessage={setErrorMessage}
      />
      <ImportFile
        apiCallMethod={async (formData) => importPurchaseOrderLineItems(purchaseOrder.id, formData)}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={(newPOLIs: IPurchaseOrderLineItem[]): void => {
          acceptLineItems(newPOLIs);
          onSave({ ...initialResponse, line_items: newPOLIs });
        }}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import purchase order CSV"
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}

        <Layout.Section variant="oneHalf">
          <PurchaseOrderEditCard
            currencies={currencies}
            disabled={isLoading}
            editDestination={editDestination}
            editVendorOrSupplier={editVendorOrSupplier}
            purchaseOrder={purchaseOrder}
            setEditDestination={setEditDestination}
            setEditVendorOrSupplier={setEditVendorOrSupplier}
            setIsDirty={setIsDirty}
            setPurchaseOrder={setPurchaseOrder}
          />
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400">
                <img src={primaryFillImage} />
                <Text as="h2" variant="headingSm">
                  Recommendations
                </Text>
              </InlineStack>
              <ForecastingForm
                arrivalDate={purchaseOrder.arrival_date}
                daysOfInventory={purchaseOrder.days_of_inventory}
                handleFormFieldChange={handleFormFieldChange}
                includeAllOutOfStockVariants={includeAllOutOfStockVariants}
                salesChannelIds={salesChannelIds}
                selectedForecastingMethod={selectedForecastingMethod}
                selectedSamplingPeriod={selectedSamplingPeriod}
              />
              {autoAddMessage ? (
                <Box>
                  <Banner onDismiss={clearAutoAddMessage}>
                    <p>{autoAddMessage}</p>
                  </Banner>
                </Box>
              ) : null}
              <InlineStack align="center">
                {readyForRecommendations ? (
                  <Button disabled={autoAddLoading} onClick={handleAutoAdd} variant="primary">
                    Auto-add variants
                  </Button>
                ) : (
                  <Tooltip content="Please wait while we finish importing your store">
                    <span>
                      <Button disabled onClick={handleAutoAdd} variant="primary">
                        Auto-add variants
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="fullWidth">
          <Card padding="0">
            <BlockStack>
              <Box padding="400" paddingBlockEnd="0">
                <InlineStack align="center">
                  <AddVariantDialog
                    active={addVariantModalActive}
                    disabled={isLoading}
                    initialHorseLocationId={purchaseOrder.horse_location_id}
                    initialSupplierId={purchaseOrder.supplier?.id}
                    initialVendor={purchaseOrder.vendor}
                    onAdd={addVariants}
                    onRemove={removeVariants}
                    removalWarning={removalWarning}
                    selectedVariantIds={selectHorseVariantIds}
                    setActive={setAddVariantModalActive}
                    setErrorMessage={setErrorMessage}
                  />
                </InlineStack>
              </Box>
              <Box position="relative">
                <Box
                  insetBlockStart="200"
                  insetInlineEnd="2400"
                  position="absolute"
                  zIndex="1"
                >
                  <CopyRecommendationsButton
                    copyBackorders={copyBackorders}
                    disabled={isLoading || !sortedLineItems.some((lineItem) => lineItem.recommended_quantity !== null)}
                    handleAutofill={handleAutofill}
                    setAllQuantities={setAllQuantities}
                    setFillQuantities={setFillQuantities}
                  />
                </Box>
                <TableControls
                  choices={draftColumns.map((column) => ({ value: column.id, label: column.title }))}
                  disabled={isDirty}
                  onChange={setSelectedColumns}
                  onSort={handleSortChange}
                  selected={selectedColumns}
                  sortOptions={sortOptions}
                  sortSelected={[sortedValue.replace("-", " ")]}
                />
              </Box>
              <IndexTable
                emptyState={emptyState}
                headings={headings}
                itemCount={sortedLineItems.length}
                loading={loadingTable}
                selectable={false}
              >
                <IndexTable.Row id="0" key={0} position={0} rowType="subheader">
                  <IndexTable.Cell>Totals</IndexTable.Cell>
                  <IndexTable.Cell />
                  {showOrderedQuantity ? <IndexTable.Cell /> : null}
                  {selectedForecastingMethod === "sales_rate" && <IndexTable.Cell />}
                  <IndexTable.Cell />
                  {showStockoutDate ? <IndexTable.Cell /> : null}
                  <IndexTable.Cell />
                  {visibleTrailingColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                  <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                  <IndexTable.Cell>{formatMoney(total.totalCost, purchaseOrder.currency)}</IndexTable.Cell>
                  <IndexTable.Cell />
                </IndexTable.Row>
                {sortedLineItems.map((poli: EditablePurchaseOrderLineItem, index) => (
                  <PurchaseOrderDraftRow
                    costExchangeRate={costExchangeRate}
                    currency={purchaseOrder.currency}
                    key={`purchase-order-draft-row:${poli.horse_variant_id}`}
                    poli={poli}
                    position={index + 1}
                    purchaseOrder={purchaseOrder}
                    readyForRecommendations={readyForRecommendations}
                    removeVariant={removeVariant}
                    selectedForecastingMethod={selectedForecastingMethod}
                    setPoli={setPoli}
                    userCurrency={userCurrency}
                    visibleColumns={visibleColumns}
                  />
                ))}
              </IndexTable>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <InlineGrid columns="1fr auto">
              <Text as="h2" variant="headingSm">
                Note to supplier
              </Text>
            </InlineGrid>
            <Box>
              <TextField
                autoComplete="off"
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                error={purchaseOrder.note?.length >= 1000 ? "Note is too long" : ""}
                label="Note"
                maxLength={1000}
                multiline={2}
                onChange={handleNoteChange}
                value={assertString(purchaseOrder.note)}
              />
            </Box>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <CostAdjustments
              costAdjustments={costAdjustments}
              currency={purchaseOrder.currency}
              disabled={isLoading}
              lineTotal={Number(total.totalCost)}
              purchaseOrder={purchaseOrder}
              setCostAdjustments={setCostAdjustments}
              setIsDirty={setSummaryIsDirty}
              total={purchaseOrderTotal}
            />
          </Card>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: openDeletionModal,
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this purchase order? This action cannot be reversed."
        deleteItem={deletePurchaseOrder}
        gotoPath="/purchase_orders"
        itemId={purchaseOrder.id}
        setActive={setDeletionDialogueActive}
        title="Delete purchase"
        toastMessage="Purchase order deleted"
      />
      {removalConfirmation}
      <div style={{ maxWidth: "640px", margin: "0 auto" }}>
        <Comments />
      </div>
      <Footer pageTitle="purchase orders" url={PURCHASE_ORDER_HELP_PAGE_URL} />
    </Page>
  );
  return <EditorContent disabled={isLoading}>{pageMarkup}</EditorContent>;

}
